import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import get from 'lodash/get'

import { IdentityDocumentInput } from './IdentityDocumentInput'
import { IdentityDocumentView } from './IdentityDocumentView'

import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { useScriptState } from '@/components/Event/NewScript/hooks/state/useScriptState'
import { AttendeeRole } from '@/constants'
import { Event, ScriptAttendee } from '@/types'

type Props = {
  attendee: ScriptAttendee
  namePrefix: string
  onSubmitClick?: (identityDocument: ScriptAttendee) => void
  event: Event
}

export const IdentityDocumentCard: React.FC<Props> = ({
  onSubmitClick,
  namePrefix,
  attendee,
  event,
}) => {
  const [edit, setEdit] = useState(false)
  const editRef = useRef(edit)
  const [viewAttendee, setViewAttendee] = useState(attendee)

  useEffect(() => {
    setViewAttendee(attendee)
  }, [attendee, event.id])
  const { clearErrors, formState, resetField } = useFormContext()
  const resetIdentityDocumentFormRef = useRef<() => void>(() => {})

  useEffect(() => {
    editRef.current = edit
  }, [edit])

  const resetIdentityDocumentForm = useCallback(() => {
    resetField(`${namePrefix}.status`, {
      defaultValue: attendee.identity_document?.status ?? null,
    })
    resetField(`${namePrefix}.document_type`, {
      defaultValue: attendee.identity_document?.document_type ?? null,
    })
    resetField(`${namePrefix}.number`, {
      defaultValue: attendee.identity_document?.number ?? null,
    })
    resetField(`${namePrefix}.photo_id_verified`, {
      defaultValue: attendee.identity_document?.photo_id_verified ?? false,
    })
    clearErrors(namePrefix)
  }, [attendee.identity_document, clearErrors, namePrefix, resetField])

  useEffect(() => {
    resetIdentityDocumentFormRef.current = resetIdentityDocumentForm
  }, [resetIdentityDocumentForm])

  useEffect(() => {
    const documentErrors = get(formState.errors, namePrefix, null)
    if (documentErrors) {
      setEdit(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.errors])

  useEffect(() => {
    return () => {
      if (editRef.current) {
        resetIdentityDocumentFormRef.current()
      }
    }
  }, [])

  const {
    actions: { updateAttendee },
  } = useScriptState()

  const onSubmitClickHandler = (submittedAttendee: ScriptAttendee) => {
    setViewAttendee(submittedAttendee)
    if (onSubmitClick) {
      onSubmitClick(submittedAttendee)
    } else {
      updateAttendee(submittedAttendee)
    }
    setEdit(false)
  }

  const onEditClickHandler = () => {
    setEdit(true)
  }

  const onCancelClick = () => {
    resetIdentityDocumentForm()
    setEdit(false)
  }

  return (
    <ActionContainer>
      {edit ? (
        <IdentityDocumentInput
          attendee={attendee}
          onSubmitClick={onSubmitClickHandler}
          onCancelClick={() => onCancelClick()}
          namePrefix={namePrefix}
          requiredDocumentNumber={attendee.role !== AttendeeRole.WITNESS}
        />
      ) : (
        <IdentityDocumentView
          attendee={viewAttendee}
          onEditClick={onEditClickHandler}
        />
      )}
    </ActionContainer>
  )
}
